import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Sparkles, Check, X, ArrowRight } from "lucide-react";
import type { PublicGeniePersona } from "@/types/discovery-catalog";
import type { BaseCapability } from "@/types/genie";
import { useCloneDemoGenie } from "@/hooks/demo/useCloneDemoGenie";
import { DemoGenieCloneService } from "@/services/demo/DemoGenieCloneService";
import { getCapability } from "@/data/genies/_capabilities";
import { getBrandForPersona } from "@/data/genies/persona-brands";
import { supabase } from "@/integrations/supabase/client";
import { getDiscoverAttribution } from "@/lib/discover-attribution";
import { DiscoverLeadCapture } from "./DiscoverLeadCapture";
import { trackEvent } from "@/lib/ga4";

interface CloneGenieOverlayProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  persona: PublicGeniePersona;
}

export const CloneGenieOverlay = ({
  open,
  onOpenChange,
  persona,
}: CloneGenieOverlayProps) => {
  const navigate = useNavigate();
  const [customName, setCustomName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  const cloneMutation = useCloneDemoGenie({
    onSuccess: () => {
      setShowSuccess(true);
      setTimeout(() => {
        navigate("/user-hub");
      }, 2500);
    },
  });

  const isCloning = cloneMutation.isPending;

  // Brand data
  const brand = getBrandForPersona(persona.id);
  const capability = getCapability(persona.capabilityType);
  const primaryColor = brand?.primaryColor || "#4E9CFF";
  const secondaryColor = brand?.secondaryColor || primaryColor;
  const safeBrandName = brand?.brandName.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const logoPath = safeBrandName ? `/brand-logos/${safeBrandName}-stacked.png` : null;

  const benefits = buildBenefits(persona, capability);

  // Enter animation
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsVisible(true));
      });
    } else {
      setIsVisible(false);
      setShowSuccess(false);
    }
  }, [open]);

  // Mobile resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Escape key
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isCloning && !showSuccess) {
        onOpenChange(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, isCloning, showSuccess, onOpenChange]);

  const handleClone = useCallback(async () => {
    trackEvent("discover_clone_started", { persona_id: persona.id });
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      trackEvent("signup_started", {
        persona_id: persona.id,
        industry: persona.industryId,
        business_type: persona.subcategoryId,
        capability: persona.capabilityType,
        ...getDiscoverAttribution(),
      });
      DemoGenieCloneService.savePendingClone({
        persona_id: persona.id,
        custom_name: customName || undefined,
        business_name: businessName || undefined,
      });
      navigate("/signup");
      return;
    }

    cloneMutation.mutate({
      persona_id: persona.id,
      custom_name: customName || undefined,
      business_name: businessName || undefined,
    });
  }, [persona.id, customName, businessName, cloneMutation, navigate]);

  if (!open) return null;

  return (
    <>
      <style>{`
        @keyframes successPulse {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.15); }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>

      <div
        className="fixed inset-0 z-50"
        role="dialog"
        aria-modal="true"
        aria-label="Create your genie"
        style={{
          opacity: isVisible ? 1 : 0,
          transition: "opacity 300ms ease-out",
        }}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={() => {
            if (!isCloning && !showSuccess) onOpenChange(false);
          }}
        />

        {/* Centered content */}
        <div className="relative z-10 flex items-center justify-center h-full p-4 sm:p-6">
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              transform: isVisible ? "scale(1)" : "scale(0.95)",
              opacity: isVisible ? 1 : 0,
              transition:
                "transform 400ms cubic-bezier(0.16, 1, 0.3, 1), opacity 300ms ease-out",
            }}
            className="w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto relative"
          >
            {/* Close button */}
            {!showSuccess && (
              <button
                onClick={() => onOpenChange(false)}
                disabled={isCloning}
                className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-white/80 hover:bg-white flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors shadow-sm"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            )}

            {showSuccess ? (
              /* ── Success State ── */
              <div
                style={{
                  padding: isMobile ? "48px 20px" : "60px 28px",
                  textAlign: "center",
                  background: `linear-gradient(160deg, ${primaryColor}08 0%, white 40%, ${secondaryColor}06 100%)`,
                }}
              >
                <div
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    background: `linear-gradient(135deg, ${primaryColor}20, ${secondaryColor}20)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px",
                    animation: "successPulse 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
                  }}
                >
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Check className="w-6 h-6 text-white" strokeWidth={3} />
                  </div>
                </div>

                <h2
                  style={{
                    fontSize: "1.4rem",
                    fontWeight: 800,
                    color: "#1F2937",
                    margin: "0 0 8px",
                  }}
                >
                  Your genie is ready!
                </h2>
                <p style={{ fontSize: "0.9rem", color: "#6B7280", margin: "0 0 4px" }}>
                  {brand?.brandName || persona.name} has been created
                </p>
                <p style={{ fontSize: "0.8rem", color: "#9CA3AF" }}>
                  Redirecting to your hub...
                </p>
              </div>
            ) : (
              /* ── Main Content ── */
              <>
                {/* Hero section */}
                <div
                  style={{
                    background: `linear-gradient(135deg, ${primaryColor}18 0%, ${secondaryColor}18 100%)`,
                    borderBottom: `1px solid ${primaryColor}15`,
                    padding: isMobile ? "20px 20px 18px" : "28px 28px 24px",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Shimmer overlay */}
                  <div
                    className="absolute inset-0 opacity-30"
                    style={{
                      background: `linear-gradient(90deg, transparent 25%, ${primaryColor}08 50%, transparent 75%)`,
                      backgroundSize: "200% 100%",
                      animation: "shimmer 3s ease-in-out infinite",
                    }}
                  />

                  {/* Logo + brand */}
                  <div className="relative flex items-center gap-3.5 mb-4">
                    <div
                      style={{
                        width: isMobile ? 48 : 56,
                        height: isMobile ? 48 : 56,
                        borderRadius: "50%",
                        background: `${primaryColor}20`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        boxShadow: `0 4px 12px ${primaryColor}25`,
                      }}
                    >
                      {logoPath && !logoError ? (
                        <img
                          src={logoPath}
                          alt={brand?.brandName || persona.name}
                          style={{ width: "60%", height: "60%", objectFit: "contain" }}
                          onError={() => setLogoError(true)}
                        />
                      ) : (
                        <Sparkles style={{ width: 24, height: 24, color: primaryColor }} />
                      )}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <p
                        style={{
                          fontSize: isMobile ? "1rem" : "1.1rem",
                          fontWeight: 700,
                          color: "#1F2937",
                          margin: 0,
                          lineHeight: 1.3,
                        }}
                      >
                        {brand?.brandName || persona.name}
                      </p>
                      {brand?.tagline && (
                        <p
                          style={{
                            fontSize: "0.8rem",
                            color: "#6B7280",
                            margin: "2px 0 0",
                            lineHeight: 1.3,
                          }}
                        >
                          {brand.tagline}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Genie name + role + capability */}
                  <div className="relative">
                    <h2
                      style={{
                        fontSize: isMobile ? "1.2rem" : "1.3rem",
                        fontWeight: 800,
                        color: "#1F2937",
                        margin: "0 0 6px 0",
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {persona.name}
                    </h2>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p style={{ fontSize: "0.85rem", color: "#6B7280", margin: 0 }}>
                        {persona.role}
                      </p>
                      <Badge
                        style={{
                          background: `${primaryColor}15`,
                          color: primaryColor,
                          border: `1px solid ${primaryColor}30`,
                          fontSize: "0.7rem",
                          fontWeight: 600,
                        }}
                      >
                        {capability?.name || persona.capabilityType}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Features section */}
                <div style={{ padding: isMobile ? "16px 20px" : "20px 28px" }}>
                  <p
                    style={{
                      fontSize: "0.72rem",
                      fontWeight: 600,
                      color: "#9CA3AF",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      marginBottom: 10,
                    }}
                  >
                    What's Included
                  </p>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                      gap: isMobile ? 6 : 8,
                    }}
                  >
                    {benefits.map((b, i) => (
                      <div
                        key={b.label}
                        style={{
                          padding: isMobile ? "9px 11px" : "10px 12px",
                          borderRadius: 10,
                          background: `${primaryColor}06`,
                          borderLeft: `3px solid ${primaryColor}`,
                          opacity: isVisible ? 1 : 0,
                          transform: isVisible ? "translateY(0)" : "translateY(8px)",
                          transition: `opacity 400ms ease-out ${200 + i * 80}ms, transform 400ms ease-out ${200 + i * 80}ms`,
                        }}
                      >
                        <p
                          style={{
                            fontSize: "0.78rem",
                            fontWeight: 600,
                            color: "#374151",
                            margin: 0,
                            lineHeight: 1.3,
                          }}
                        >
                          {b.label}
                        </p>
                        <p
                          style={{
                            fontSize: "0.68rem",
                            color: "#9CA3AF",
                            margin: "2px 0 0",
                            lineHeight: 1.3,
                          }}
                        >
                          {b.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Customize section */}
                <div
                  style={{
                    padding: isMobile ? "0 20px 16px" : "0 28px 20px",
                    borderTop: "1px solid #f3f4f6",
                  }}
                >
                  <div style={{ paddingTop: isMobile ? 16 : 20 }}>
                    <p
                      className="flex items-center gap-2"
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        color: "#374151",
                        marginBottom: 14,
                      }}
                    >
                      <Sparkles style={{ width: 14, height: 14, color: primaryColor }} />
                      Customize (optional)
                    </p>

                    <div style={{ marginBottom: 12 }}>
                      <Label
                        htmlFor="businessName"
                        style={{ fontSize: "0.78rem", fontWeight: 500, color: "#6B7280" }}
                      >
                        Your Business Name
                      </Label>
                      <Input
                        id="businessName"
                        placeholder="e.g., Smith's HVAC Services"
                        value={businessName}
                        onChange={(e) => setBusinessName(e.target.value)}
                        disabled={isCloning}
                        className="mt-1"
                        style={{ borderRadius: 10 }}
                      />
                      <p
                        style={{
                          fontSize: "0.7rem",
                          color: "#9CA3AF",
                          marginTop: 4,
                        }}
                      >
                        We'll personalize the genie's greeting and responses for your business
                      </p>
                    </div>

                    <div>
                      <Label
                        htmlFor="customName"
                        style={{ fontSize: "0.78rem", fontWeight: 500, color: "#6B7280" }}
                      >
                        Custom Genie Name
                      </Label>
                      <Input
                        id="customName"
                        placeholder={`e.g., ${persona.name}`}
                        value={customName}
                        onChange={(e) => setCustomName(e.target.value)}
                        disabled={isCloning}
                        className="mt-1"
                        style={{ borderRadius: 10 }}
                      />
                    </div>
                  </div>
                </div>

                {/* CTA section */}
                <div style={{ padding: isMobile ? "0 20px 24px" : "0 28px 28px" }}>
                  {/* Soft email capture — additive, never gates the signup path */}
                  <DiscoverLeadCapture
                    personaId={persona.id}
                    demoUrl={typeof window !== "undefined" ? window.location.href : undefined}
                    captureLocation="clone_overlay"
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                  />

                  {/* — or — divider */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      margin: "0 0 14px",
                    }}
                  >
                    <div style={{ flex: 1, height: 1, background: "#F3F4F6" }} />
                    <span style={{ fontSize: "0.72rem", color: "#9CA3AF" }}>or</span>
                    <div style={{ flex: 1, height: 1, background: "#F3F4F6" }} />
                  </div>

                  <Button
                    size="lg"
                    onClick={handleClone}
                    disabled={isCloning}
                    style={{
                      width: "100%",
                      background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                      color: "white",
                      fontWeight: 700,
                      borderRadius: 24,
                      padding: "14px 24px",
                      fontSize: "1rem",
                      boxShadow: `0 4px 16px ${primaryColor}35`,
                      transition: "all 0.2s ease",
                      border: "none",
                    }}
                    onMouseOver={(e) => {
                      if (!isCloning) {
                        e.currentTarget.style.transform = "translateY(-2px) scale(1.02)";
                        e.currentTarget.style.boxShadow = `0 6px 24px ${primaryColor}50`;
                      }
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = "translateY(0) scale(1)";
                      e.currentTarget.style.boxShadow = `0 4px 16px ${primaryColor}35`;
                    }}
                  >
                    {isCloning ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating Your Genie...
                      </>
                    ) : (
                      <>
                        Create My Genie
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>

                  <p
                    style={{
                      textAlign: "center",
                      fontSize: "0.72rem",
                      color: "#9CA3AF",
                      marginTop: 14,
                      letterSpacing: "0.02em",
                    }}
                  >
                    No credit card required · Ready in 60 seconds · Customize everything
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

/** Build benefits list from persona data */
function buildBenefits(
  persona: PublicGeniePersona,
  capability: BaseCapability | undefined,
) {
  const benefits: Array<{ label: string; description: string }> = [];

  if (capability) {
    benefits.push({
      label: capability.name,
      description:
        capability.description.length > 60
          ? capability.description.substring(0, 57) + "..."
          : capability.description,
    });
  }

  if (persona.config.collectFields && persona.config.collectFields.length > 0) {
    benefits.push({
      label: "Lead Capture",
      description: "Collects contact details during the call",
    });
  }

  if (persona.config.afterHours) {
    benefits.push({
      label: "24/7 Available",
      description: "Handles calls even after business hours",
    });
  }

  if (
    persona.config.escalationRules &&
    persona.config.escalationRules.length > 0
  ) {
    benefits.push({
      label: "Smart Escalation",
      description: "Routes urgent calls to the right person",
    });
  }

  benefits.push({
    label: "Fully Customizable",
    description: "Adjust voice, personality, and responses",
  });

  return benefits;
}
